import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { getSessionUser } from "../../../../utils/auth/sessionUser";
import { Input, Button, Table, Tabs, Modal, Space, Tooltip, Checkbox, Dropdown, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, SettingOutlined, SearchOutlined, LinkOutlined } from "@ant-design/icons";

import { Appointment, AppointmentType, AppointmentTypeUsage } from "../../../../types/appointments/AppointmentTypes";
import { TeamMember, defaultTeamMember } from "../../../../types/admin/TeamTypes";
import { Patient } from "../../../../types/healthcare/PatientTypes";
import { deleteAppointment, fetchAppointments, fetchAppointmentTypes, fetchPatientAppointmentTypeUsage, getPatientAppointmentTypes, assignAppointmentTypeToPatient, removeAppointmentTypeFromPatient } from "../../../../api/healthcare/Appointment/AppointmentApi";
import { fetchTeamMembers } from "../../../../api/admin/teamApi";
import AppointmentModal from "../../appointments/AppointmentDrawer";
import BookingLinkModal from "../../appointments/BookingLinkModal";
import { useNotifications } from "../../../../context/NotificationContext";
import SectionCard from "../../../../components/shared/SectionCard";
import InlineLoading from "../../../../components/shared/InlineLoading";
import StatusTag from "../../../../components/shared/StatusTag";
import type { UserUuid } from "../../../../types/identity";

const { Text } = Typography;

interface AppointmentTabProps {
	patient: Patient;
}

const AppointmentTab: React.FC<AppointmentTabProps> = ({ patient }) => {
	const user = getSessionUser();

	const { showSuccess, showError, showInfo } = useNotifications();

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [appointments, setAppointments] = useState<Appointment[]>([]);
	const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
	const [pastAppointments, setPastAppointments] = useState<Appointment[]>([]);
	const [canceledAppointments, setCanceledAppointments] = useState<Appointment[]>([]);
	const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
	const [selectedTeamMember, setSelectedTeamMember] = useState<TeamMember | null>(defaultTeamMember);
	const [activeTab, setActiveTab] = useState<string>("all");
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
	const [selectedAppointment, setSelectedAppointment] = useState<Appointment | undefined>(undefined);
	const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState<boolean>(false);
	// S8 (ClickUp 86d3yhdw6) — the primary entry point for handing a booking link to the patient.
	const [isBookingLinkModalOpen, setIsBookingLinkModalOpen] = useState<boolean>(false);
	const [allAppointmentTypes, setAllAppointmentTypes] = useState<AppointmentType[]>([]);
	const [patientAppointmentTypes, setPatientAppointmentTypes] = useState<AppointmentType[]>([]);
	const [isAppointmentTypesLoading, setIsAppointmentTypesLoading] = useState<boolean>(false);
	const [selectedAppointmentTypeIds, setSelectedAppointmentTypeIds] = useState<number[]>([]);
	// CAPPED types only — an uncapped type is absent, and that absence is what makes the
	// "Used N of M" line render nothing rather than "Used 3 of unlimited" (S10, 86d3yhdwc).
	const [appointmentTypeUsage, setAppointmentTypeUsage] = useState<AppointmentTypeUsage[]>([]);

	useEffect(() => {
		if (patient?.id) {
			loadAppointments();
			loadAppointmentTypes();
		}
	}, [patient]);

	const loadAppointmentTypes = async () => {
		setIsAppointmentTypesLoading(true);
		try {
			const allTypes = await fetchAppointmentTypes();
			setAllAppointmentTypes(allTypes);
			const patientTypes = await getPatientAppointmentTypes(patient.id);
			setPatientAppointmentTypes(patientTypes);
			setSelectedAppointmentTypeIds(patientTypes.map((t) => t.id));
			setAppointmentTypeUsage(await fetchPatientAppointmentTypeUsage(patient.id));
		} catch {
			showError("Failed to load appointment types");
		} finally {
			setIsAppointmentTypesLoading(false);
		}
	};

	/** The patient's usage of a CAPPED type, or undefined when the type has no cap. */
	const usageFor = (appointmentTypeId: number) => appointmentTypeUsage.find((u) => u.appointment_type_id === appointmentTypeId);

	const loadAppointments = async () => {
		setIsLoading(true);
		try {
			const { data: appointmentData } = await fetchAppointments(undefined, null, null, patient.id);
			const list: Appointment[] = appointmentData || [];
			setAppointments(list);
			const now = new Date();
			setUpcomingAppointments(list.filter((a) => new Date(a.start_time as Date) > now && a.status !== "Canceled"));
			setPastAppointments(list.filter((a) => new Date(a.start_time as Date) <= now && a.status !== "Canceled"));
			setCanceledAppointments(list.filter((a) => a.status === "Canceled" || a.status === "Canceled within 24h"));
			try {
				const { data: members } = await fetchTeamMembers(1, 100);
				setTeamMembers(members || []);
			} catch {}
		} catch {
			showError("Failed to load appointments");
		} finally {
			setIsLoading(false);
		}
	};

	const createNewAppointment = () => {
		const newAppt: Appointment = {
			created_at: new Date(),
			date: new Date(),
			end_time: new Date(),
			start_time: new Date(),
			id: 0,
			patient_id: patient.id,
			practitioner_id: "",
			status: "Scheduled",
			updated_at: new Date(),
			title: "",
			category: "",
			location: "Online",
			room_number: "",
			consultation_link: "",
		} as Appointment;
		setSelectedAppointment(newAppt);
		setIsAppointmentModalOpen(true);
	};

	const handleDelete = (appointment: Appointment) => {
		Modal.confirm({
			title: "Delete appointment",
			icon: <ExclamationCircleOutlined />,
			content: "Are you sure you want to delete this appointment?",
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					await deleteAppointment(appointment.id);
					showSuccess("Appointment deleted");
					await loadAppointments();
				} catch {
					showError("Failed to delete appointment");
				}
			},
		});
	};

	const handleAppointmentTypeChange = (id: number, checked: boolean) => {
		setSelectedAppointmentTypeIds((prev) => (checked ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((x) => x !== id)));
	};

	const saveAppointmentTypeChanges = async () => {
		try {
			const currentIds = patientAppointmentTypes.map((t) => t.id);
			const toAssign = selectedAppointmentTypeIds.filter((id) => !currentIds.includes(id));
			const toRemove = currentIds.filter((id) => !selectedAppointmentTypeIds.includes(id));
			await assignAppointmentTypeToPatient(toAssign, patient.id);
			if (toRemove.length > 0) await removeAppointmentTypeFromPatient(toRemove, patient.id);
			if (toAssign.length > 0 || toRemove.length > 0) {
				showSuccess("Appointment type assignments updated");
				await loadAppointmentTypes();
			}
		} catch {
			showError("Failed to update appointment type assignments");
		}
	};

	const dateRenderer = (value?: string) => (value ? format(new Date(value), "MMM dd, yyyy hh:mm a") : null);
	const statusTag = (status?: string) => <StatusTag status={status ?? ""} />;
	const practitionerName = (id?: UserUuid) => {
		const p = teamMembers.find((m) => m.id === id);
		return p ? `${p.first_name} ${p.last_name}` : null;
	};

	const getFilteredAppointments = (list: Appointment[]) => {
		if (!searchTerm) return list;
		const q = searchTerm.toLowerCase();
		return list.filter((a) => (a.title || "").toLowerCase().includes(q) || (practitionerName(a.practitioner_id) || "").toLowerCase().includes(q));
	};

	const expandedRowRender = (record: Appointment) => (
		<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
			<div style={{ padding: 12, background: "var(--mg-bg-subtle)", borderRadius: 8, border: "1px solid var(--mg-border)" }}>
				<div style={{ marginBottom: 8 }}>
					<Text strong style={{ fontSize: 12 }}>
						Status
					</Text>
					<div style={{ marginTop: 4 }}>{statusTag(record.status)}</div>
				</div>
				<div style={{ marginBottom: 8 }}>
					<Text strong style={{ fontSize: 12 }}>
						Location
					</Text>
					<div style={{ marginTop: 4 }}>
						<Text style={{ fontSize: 13 }}>{record.location || "Not specified"}</Text>
					</div>
				</div>
				<div style={{ marginBottom: 8 }}>
					<Text strong style={{ fontSize: 12 }}>
						Start
					</Text>
					<div style={{ marginTop: 4 }}>
						<Text style={{ fontSize: 13 }}>{dateRenderer(record.start_time as unknown as string)}</Text>
					</div>
				</div>
				<div>
					<Text strong style={{ fontSize: 12 }}>
						End
					</Text>
					<div style={{ marginTop: 4 }}>
						<Text style={{ fontSize: 13 }}>{dateRenderer(record.end_time as unknown as string)}</Text>
					</div>
				</div>
			</div>
			<div style={{ padding: 12, background: "var(--mg-bg-subtle)", borderRadius: 8, border: "1px solid var(--mg-border)" }}>
				<div style={{ marginBottom: 8 }}>
					<Text strong style={{ fontSize: 12 }}>
						Practitioner
					</Text>
					<div style={{ marginTop: 4 }}>
						<Text style={{ fontSize: 13 }}>{practitionerName(record.practitioner_id) || "Not assigned"}</Text>
					</div>
				</div>
				<div style={{ marginBottom: 8 }}>
					<Text strong style={{ fontSize: 12 }}>
						Category
					</Text>
					<div style={{ marginTop: 4 }}>
						<Text style={{ fontSize: 13 }}>{record.category || "Not specified"}</Text>
					</div>
				</div>
				<div style={{ marginBottom: 8 }}>
					<Text strong style={{ fontSize: 12 }}>
						Created
					</Text>
					<div style={{ marginTop: 4 }}>
						<Text style={{ fontSize: 13 }}>{record.created_at ? format(new Date(record.created_at as Date), "MMM dd, yyyy") : "Not available"}</Text>
					</div>
				</div>
				{record.consultation_link && (
					<div>
						<Text strong style={{ fontSize: 12 }}>
							Consultation Link
						</Text>
						<div style={{ marginTop: 4 }}>
							<a href={record.consultation_link} target="_blank" rel="noreferrer">
								{record.consultation_link}
							</a>
						</div>
					</div>
				)}
			</div>
		</div>
	);

	const columns: ColumnsType<Appointment> = [
		{
			title: "Appointment",
			dataIndex: "title",
			key: "title",
			render: (_val, record) => (
				<div>
					<Text strong style={{ display: "block" }}>
						{record.title}
					</Text>
					<Text type="secondary" style={{ fontSize: 12 }}>
						{practitionerName(record.practitioner_id)}
					</Text>
				</div>
			),
			sorter: (a, b) => String(a.title || "").localeCompare(String(b.title || "")),
		},
		{ title: "Status", dataIndex: "status", key: "status", render: (v) => statusTag(v as string), width: 140 },
		{ title: "Date & Time", dataIndex: "start_time", key: "start_time", render: (v) => dateRenderer(v as string), width: 220 },
		{ title: "Location", dataIndex: "location", key: "location", render: (v) => v || "—", width: 140 },
		{
			title: "Actions",
			key: "actions",
			width: 100,
			fixed: "right",
			render: (_: any, record) => (
				<Space size="small">
					<Tooltip title="Edit">
						<Button
							type="text"
							icon={<EditOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								setSelectedAppointment(record);
								setIsAppointmentModalOpen(true);
							}}
						/>
					</Tooltip>
					<Tooltip title="Delete">
						<Button
							type="text"
							danger
							icon={<DeleteOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								handleDelete(record);
							}}
						/>
					</Tooltip>
				</Space>
			),
		},
	];

	const expandable = { expandedRowRender, rowExpandable: () => true, expandedRowKeys, onExpandedRowsChange: (keys: React.Key[]) => setExpandedRowKeys([...keys]) };

	const tableProps = { size: "small" as const, columns, pagination: { pageSize: 10 }, expandable, scroll: { x: "max-content" as const } };

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
				<Space>
					<Button type="primary" icon={<PlusOutlined />} onClick={createNewAppointment}>
						New Appointment
					</Button>
					<Button icon={<LinkOutlined />} data-testid="appointments-booking-link-btn" onClick={() => setIsBookingLinkModalOpen(true)}>
						Booking Link
					</Button>
					<Dropdown
						overlay={
							<div
								style={{
									background: "var(--mg-bg-elevated)",
									border: "1px solid var(--mg-border)",
									borderRadius: 6,
									padding: 12,
									minWidth: 280,
									maxHeight: 400,
									overflowY: "auto",
									boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
								}}
							>
								<Text strong style={{ display: "block", marginBottom: 12, fontSize: 14 }}>
									Manage Appointment Types
								</Text>
								{isAppointmentTypesLoading ? (
									<InlineLoading minHeight={60} />
								) : allAppointmentTypes.length === 0 ? (
									<Text type="secondary" style={{ fontSize: 12 }}>
										No appointment types available
									</Text>
								) : (
									<>
										{allAppointmentTypes.map((type) => (
											<div key={type.id} style={{ marginBottom: 8, display: "flex", alignItems: "flex-start", gap: 8 }}>
												<Checkbox checked={selectedAppointmentTypeIds.includes(type.id) || type.allow_online_booking} onChange={(e) => handleAppointmentTypeChange(type.id, e.target.checked)} />
												<div>
													<Text style={{ fontSize: 13, fontWeight: 500, display: "block" }}>{type.name}</Text>
													{type.description && (
														<Text type="secondary" style={{ fontSize: 11 }}>
															{type.description}
														</Text>
													)}
													<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
														{type.duration_minutes} min{type.telehealth_enabled && " • Telehealth"}
													</Text>
													{/*
														S10 (ClickUp 86d3yhdwc) — shown for CAPPED types only. A
														practitioner-created booking counts toward the patient's
														usage even though the cap never blocks a practitioner, and
														this line is where that becomes visible.
													*/}
													{usageFor(type.id) && (
														<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
															Used {usageFor(type.id)!.bookings_used} of {usageFor(type.id)!.max_bookings_per_patient}
														</Text>
													)}
												</div>
											</div>
										))}
										<div style={{ borderTop: "1px solid var(--mg-border)", paddingTop: 8, display: "flex", justifyContent: "flex-end" }}>
											<Button type="primary" size="small" onClick={saveAppointmentTypeChanges}>
												Save Changes
											</Button>
										</div>
									</>
								)}
							</div>
						}
						trigger={["click"]}
						placement="bottomLeft"
					>
						<Button icon={<SettingOutlined />}>Manage Types</Button>
					</Dropdown>
				</Space>
				<Input placeholder="Search appointments" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} prefix={<SearchOutlined />} allowClear style={{ minWidth: 240 }} />
			</div>

			<>
				{isLoading ? (
					<InlineLoading message="Loading appointments…" minHeight={200} />
				) : (
					<Tabs
						activeKey={activeTab}
						onChange={setActiveTab}
						items={[
							{ key: "all", label: "All Appointments", children: <Table<Appointment> rowKey={(r) => String(r.id)} dataSource={getFilteredAppointments(appointments)} {...tableProps} /> },
							{ key: "upcoming", label: "Upcoming", children: <Table<Appointment> rowKey={(r) => String(r.id)} dataSource={getFilteredAppointments(upcomingAppointments)} {...tableProps} /> },
							{ key: "past", label: "Past", children: <Table<Appointment> rowKey={(r) => String(r.id)} dataSource={getFilteredAppointments(pastAppointments)} {...tableProps} /> },
							{ key: "canceled", label: "Canceled", children: <Table<Appointment> rowKey={(r) => String(r.id)} dataSource={getFilteredAppointments(canceledAppointments)} {...tableProps} /> },
						]}
					/>
				)}
			</>

			{isBookingLinkModalOpen && (
				<BookingLinkModal
					isOpen={isBookingLinkModalOpen}
					onClose={() => setIsBookingLinkModalOpen(false)}
					patient={patient}
				/>
			)}

			{isAppointmentModalOpen && selectedAppointment && (
				<AppointmentModal
					user={user}
					isOpen={isAppointmentModalOpen}
					onClose={() => setIsAppointmentModalOpen(false)}
					onStartConsultation={() => {}}
					selectedAppointment={selectedAppointment}
					teamMembers={teamMembers}
					selectedTeamMember={selectedTeamMember}
					setSelectedTeamMember={setSelectedTeamMember}
					setSelectedAppointment={setSelectedAppointment}
					setSelectedPatient={() => {}}
					selectedPatient={patient}
				/>
			)}
		</div>
	);
};

export default AppointmentTab;
